import { useEffect, useRef } from 'react';

import { Instant } from '@js-joda/core';
import type { Location } from '@remix-run/react';
import { useLocation } from '@remix-run/react';
import { useObservability } from 'platform/observability';
import { normalizePathname, searchParamsToObject } from 'shared/hooks/usePath';

/**
 * Enumeration of page states.
 *
 * LOADING is the only special case, where page view events will NOT be recorded.
 *
 * See documentation of individual page states for descriptions.
 */
export enum PageState {
  /**
   * A page is still loading. A page view event will NOT be recorded.
   *
   * If a page transitions from LOADING to another state, the next view event will include the loading time.
   */
  LOADING = 'loading',
  /** A page that has loaded normally. */
  LOADED = 'loaded',
}

interface EventData {
  resource: 'cake';
  pageState: PageState;
  path: string;
  pathname: string;
  queryParams: Record<string, string>;
  hash: string;
}

/**
 * This hook tracks top-level page view events for each page state OR path change EXCEPT in the LOADING state.
 *
 * A view event looks like this:
 *  category: "page"
 *  action: "view"
 *  label: <path>
 *
 * The data attached to each event is roughly the same:
 *  {
 *    resource: "cake" (this is hardcoded to know it's Cake),
 *    pageState: PageState (e.g. 'loaded')
 *    path: string (normalized pathname, without leading and trailing slashes) (e.g. "path/subpath")
 *    pathname: string (raw pathname) (e.g. "/path/subpath/")
 *    search: string (e.g. "?foo=bar")
 *    hash: string (e.g. "#header")
 *  }
 */
export const useAutoTrackPageViews = (pageState: PageState) => {
  const location = useLocation();
  return useAutoTrackPageViewsInternal(pageState, location);
};

/**
 * This is exported only for the test. It's for testing rerendering with explicit location values.
 * @internal
 */
export const useAutoTrackPageViewsInternal = (pageState: PageState, location: Location) => {
  const normalizedPath = normalizePathname(location.pathname);
  const { trackEvent, trackJourneyEvent } = useObservability();

  // lastLoadingData is used to compute the loadingDuration for all NON-loading page states.
  // The path is required to make sure the path is the same.
  // The startTime is required to compute the duration.
  const lastLoadingData = useRef<{
    normalizedPath: string;
    startTime: Instant;
  }>();

  // Send page view events to Journey every time the URL changes
  const initPath = useRef<string>();
  useEffect(() => {
    if (initPath.current !== normalizedPath) {
      trackJourneyEvent('platform', 'pageViewEvent', {});
      initPath.current = normalizedPath;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
  }, [normalizedPath]);

  useEffect(
    () => {
      // LOADING is the special case. Keep lastLoadingData up-to-date, and don't track any events.
      if (pageState === PageState.LOADING) {
        lastLoadingData.current = {
          normalizedPath,
          startTime: Instant.now(),
        };
        return;
      }

      // If not LOADING, track events.

      // Build data that will be tracked in events.
      const data: EventData = {
        resource: 'cake',
        pageState,
        path: normalizedPath,
        pathname: location.pathname,
        queryParams: searchParamsToObject(location.search),
        hash: location.hash,
      };
      // loadingDurationMs is set if previous page was LOADING AND on the SAME path.
      if (lastLoadingData.current && lastLoadingData.current.normalizedPath === normalizedPath) {
        // Unset lastLoadingData once consumed.
        lastLoadingData.current = undefined;
      }

      // Track page view event.
      trackEvent('view', normalizedPath, { ...data }, { categoryOverride: 'page' });
    },
    // We only care about pageState and normalizedPath changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [pageState, normalizedPath],
  );
};
